/**
 * Stamps (docs/archive/OPEN_WORLD.md §4.1): an authored map placed into a region at an origin, its layers
 * copied byte for byte. The village (`buildAuthored(yanyu)`, built into its GameMap by
 * `generateZone('yanyu')`) is stamped with its tile (0, 0) at the sketch's `core`.
 *
 * The stamped data stays in its own (village-local) coordinates; `shiftMap` moves the map's
 * structures, flights, landmarks, regions, props and warps by the origin for the region's GameMap.
 */
import {
  deepAt,
  edgeAt,
  flightIdAt,
  levelCm,
  madeAt,
  ownerAt,
  safeAt,
  surfKind,
  tileAt,
  variantAt,
  type GameMap,
  type MapFlight,
  type MapProp,
  type Point,
  type Region as Rect,
  type Structure,
} from '../map';

/** An authored map's layers, ready to copy into chunks (levels in centimetres). */
export interface RegionStamp {
  /** Where its tile (0, 0) lands in the region. */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly tiles: Uint8Array;
  readonly variant: Uint8Array;
  readonly surface: Uint8Array;
  readonly level: Int32Array;
  readonly edge: Uint8Array;
  readonly owner: Uint16Array;
  readonly flight: Uint16Array;
  readonly safe: Uint8Array;
  readonly made: Uint8Array;
  readonly deep: Uint8Array;
}

/** The layers of an authored map (read through the accessors) placed at (x, y). */
export function stampOf(m: GameMap, x: number, y: number): RegionStamp {
  const { w, h } = m;
  const n = w * h;
  const s = {
    x,
    y,
    w,
    h,
    tiles: new Uint8Array(n),
    variant: new Uint8Array(n),
    surface: new Uint8Array(n),
    level: new Int32Array(n),
    edge: new Uint8Array(n),
    owner: new Uint16Array(n),
    flight: new Uint16Array(n),
    safe: new Uint8Array(n),
    made: new Uint8Array(n),
    deep: new Uint8Array(n),
  };
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++) {
      const q = j * w + i;
      s.tiles[q] = tileAt(m, i, j);
      s.variant[q] = variantAt(m, i, j);
      s.surface[q] = surfKind(m, i, j);
      s.level[q] = levelCm(m, i, j);
      s.edge[q] = edgeAt(m, i, j);
      s.owner[q] = ownerAt(m, i, j);
      s.flight[q] = flightIdAt(m, i, j);
      s.safe[q] = safeAt(m, i, j);
      s.made[q] = madeAt(m, i, j);
      s.deep[q] = deepAt(m, i, j);
    }
  return s;
}

const pt = (p: Point, dx: number, dy: number): Point => ({ x: p.x + dx, y: p.y + dy });
const rect = (r: Rect, dx: number, dy: number): Rect => [r[0] + dx, r[1] + dx, r[2] + dy, r[3] + dy];

export function shiftStructure(st: Structure, dx: number, dy: number): Structure {
  return {
    ...st,
    i: st.i + dx,
    j: st.j + dy,
    ...(st.door ? { door: { ...st.door, i: st.door.i + dx, j: st.door.j + dy } } : {}),
  };
}

export function shiftFlight(f: MapFlight, dx: number, dy: number): MapFlight {
  return {
    ...f,
    rect: rect(f.rect, dx, dy),
    ...(f.path ? { path: { ...f.path, pts: f.path.pts.map(([x, y]) => [x + dx, y + dy] as const) } } : {}),
  };
}

/**
 * The parts of an authored map that carry coordinates, moved by (dx, dy): its named points,
 * regions, structures, flights, landmarks, props and reserved warps.
 */
export function shiftMap(m: GameMap, dx: number, dy: number) {
  return {
    plaza: pt(m.plaza, dx, dy),
    lair: pt(m.lair, dx, dy),
    spawn: pt(m.spawn, dx, dy),
    elder: pt(m.elder, dx, dy),
    jail: pt(m.jail, dx, dy),
    arena: pt(m.arena, dx, dy),
    duel: pt(m.duel, dx, dy),
    regions: {
      wolves: rect(m.regions.wolves, dx, dy),
      bandits: rect(m.regions.bandits, dx, dy),
      west: rect(m.regions.west, dx, dy),
      east: rect(m.regions.east, dx, dy),
      graves: rect(m.regions.graves, dx, dy),
    },
    structures: m.structures.map((s) => shiftStructure(s, dx, dy)),
    flights: (m.flights ?? []).map((f) => shiftFlight(f, dx, dy)),
    landmarks: Object.fromEntries(Object.entries(m.landmarks ?? {}).map(([k, p]) => [k, pt(p, dx, dy)])),
    props: (m.props ?? []).map((p): MapProp => ({ ...p, x: p.x + dx, y: p.y + dy })),
    reserved: (m.reserved ?? []).map((r) => ({ ...r, x: r.x + dx, y: r.y + dy })),
  };
}
